import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges, OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { ShiftWithDriverAndVehicleModel } from '../../models/shift.model';
import { RouteAssignment } from '../../models/route-assignment';
import { DatePipe, NgStyle } from '@angular/common';
import moment, { Moment } from 'moment';
import { MatTooltip } from '@angular/material/tooltip';
import { MatButton } from '@angular/material/button';
import { MatMenu, MatMenuItem, MatMenuTrigger } from '@angular/material/menu';
import { ActivityFilter } from '../../../configuration/settings.service';
import { MatIcon } from '@angular/material/icon';
import { ShiftMapContent } from '../map-preview/model/map-content/ShiftMapContent';
import { FeatureCollection } from 'geojson';
import { SensorType } from '../../models/vehicle.model';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ShiftMapDataService } from '../../../pages/shift-map/services/shift-map-data.service';

export class BarSection {
  name?: string;
  left: number;
  width: number;
}

enum PlaybackAction {
  STOP = 'stop',
  PLAY = 'play',
}

enum PlaybackStatus {
  STOPPED = 'stopped',
  PLAYING = 'playing',
  LOADING = 'loading'
}

interface TimeAxisMarker {
  position: number;
  label: string;
  isStart?: boolean;
  isEnd?: boolean;
}

@Component({
  selector: 'app-time-line-control',
  imports: [
    DatePipe,
    NgStyle,
    MatTooltip,
    MatButton,
    MatMenuTrigger,
    MatMenu,
    MatMenuItem,
    MatIcon,
    MatProgressSpinner,
  ],
  templateUrl: './time-line-control.component.html',
  styleUrl: './time-line-control.component.scss'
})
export class TimeLineControlComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() shiftMapContent: ShiftMapContent;
  @Input() shift: ShiftWithDriverAndVehicleModel;
  @Input() shiftAssignments: RouteAssignment[];
  @Input() assignment: RouteAssignment;
  @Input() shiftTrack: FeatureCollection;
  @Input() time: number; // this can be decimal when animating shift progress
  @Output() timeChanged = new EventEmitter<number>();
  @Output() speedChanged = new EventEmitter<number>();

  barWidthPx: number;
  markerPositionPx = 0;
  markerFromEndPx = 0;
  currentTimestamp: Date;
  isDragging = false;

  timeAxisMarkers: TimeAxisMarker[] = [];

  speed = 10;
  speeds = [5, 10, 20];

  assignmentItems: BarSection[];
  plowItems: BarSection[];
  spreaderItems: BarSection[];
  sweepingItems: BarSection[];
  mowingItems: BarSection[];

  playbackStatus: PlaybackStatus = PlaybackStatus.STOPPED;

  private resizeObserver?: ResizeObserver;
  private resizeTimeout?: number;
  private readonly RESIZE_DEBOUNCE_MS = 200;
  timeLineBarWidth: number;

  PlaybackAction = PlaybackAction;
  PlaybackStatus = PlaybackStatus;

  @ViewChild('timeLineBar') private timeLineBar: ElementRef<HTMLElement>;

  constructor(
    private readonly shiftMapDataManager: ShiftMapDataService,
  ) {
  }

  ngOnInit() {
    this.currentTimestamp = new Date(this.assignment ? this.assignment.onAssignmentFrom : this.shift.start);
    this.shiftMapDataManager.shiftAnimationEndReached$.subscribe(() => {
      this.playbackStatus = PlaybackStatus.STOPPED;
    });
  }

  ngOnChanges(changes: SimpleChanges) {
    const shiftAssignments = changes.shiftAssignments?.currentValue;
    if (shiftAssignments) {
      this.assignmentItems = (shiftAssignments as RouteAssignment[])
        .map(a => {
          return this.toBarSection(
            a.routeName,
            moment(a.onAssignmentFrom).unix(),
            moment(a.completed).unix(),
            moment(this.shift.start).unix(),
            moment(this.shift.end).unix(),
          );
        })
        .filter(a => !!a);
    }

    const shiftTrackGeometry = changes.shiftTrack?.currentValue;
    if (shiftTrackGeometry) {
      const lineStringsProps: {flags: number, time_end: number, time_start: number}[] = shiftTrackGeometry.features.map(f => {
        return {
          flags: f.properties.flags,
          time_end: f.properties.time_end,
          time_start: f.properties.time_start,
        };
      });
      this.plowItems = lineStringsProps
        .filter(props => ActivityFilter.flagCheck(ActivityFilter.PLOWING, props.flags))
        .map(props => {
          return this.toBarSection(
            undefined,
            props.time_start,
            props.time_end,
            this.getTimeLineStartMoment().unix(),
            this.getTimeLineEndMoment().unix(),
          );
        });
      this.spreaderItems = lineStringsProps
        .filter(props => ActivityFilter.flagCheck(ActivityFilter.SPREADING, props.flags))
        .map(props => {
          return this.toBarSection(
            undefined,
            props.time_start,
            props.time_end,
            this.getTimeLineStartMoment().unix(),
            this.getTimeLineEndMoment().unix(),
          );
        });
      this.sweepingItems = lineStringsProps
        .filter(props => ActivityFilter.flagCheck(ActivityFilter.SWEEPING, props.flags))
        .map(props => {
          return this.toBarSection(
            undefined,
            props.time_start,
            props.time_end,
            this.getTimeLineStartMoment().unix(),
            this.getTimeLineEndMoment().unix(),
          );
        });
      this.mowingItems = lineStringsProps
        .filter(props => ActivityFilter.flagCheck(ActivityFilter.MOWING, props.flags))
        .map(props => {
          return this.toBarSection(
            undefined,
            props.time_start,
            props.time_end,
            this.getTimeLineStartMoment().unix(),
            this.getTimeLineEndMoment().unix(),
          );
        });
    }

    const vehicleMarkerUnixTimeChange = changes.time;
    if (vehicleMarkerUnixTimeChange?.currentValue &&
      (vehicleMarkerUnixTimeChange?.previousValue !== vehicleMarkerUnixTimeChange.currentValue)
    ) {
      this.vehicleMarkerMoved(vehicleMarkerUnixTimeChange.currentValue);
    }

    // switching between assignment and shift view
    if (changes.assignment) {
      this.updateMarkers(
        this.timeLineBarWidth,
        this.getTimeLineStartMoment(),
        this.getTimeLineEndMoment(),
      );
    }
  }

  ngAfterViewInit() {
    // window resize or other parent component resize
    this.resizeObserver = new ResizeObserver(entries => {
      for (const entry of entries) {
        const newWidth = entry.contentRect.width;
        if (newWidth !== this.timeLineBarWidth) {
          this.timeLineBarWidth = newWidth;

          // Debounce the update
          if (this.resizeTimeout) {
            clearTimeout(this.resizeTimeout);
          }

          this.resizeTimeout = window.setTimeout(() => {
            this.updateMarkers(
              this.timeLineBarWidth,
              this.getTimeLineStartMoment(),
              this.getTimeLineEndMoment(),
            );
          }, this.RESIZE_DEBOUNCE_MS);
        }
      }
    });

    this.resizeObserver.observe(this.timeLineBar.nativeElement);
  }

  ngOnDestroy() {
    // Cleanup listeners if component is destroyed during drag
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);

    // clear resize timeout
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
    this.resizeObserver?.disconnect();
  }

  private toBarSection(name: string, fromUnixTime: number, toUnixTime: number, startUnixTime: number, endUnixTime: number): BarSection {
    const percentBase = endUnixTime - startUnixTime;
    if (percentBase === 0) {
      return null;
    }
    const left = (fromUnixTime - startUnixTime) / percentBase * 100;
    const width = (toUnixTime - startUnixTime) / percentBase * 100 - left;
    return {
      name,
      width,
      left,
    };
  }

  private getTimeLineStartMoment(): moment.Moment {
    return moment(this.assignment ? this.assignment.onAssignmentFrom : this.shift.start);
  }

  private getTimeLineEndMoment(): moment.Moment {
    return moment(this.assignment ? this.assignment.completed : this.shift.end);
  }

  vehicleMarkerMoved(unixtime: number) {
    this.currentTimestamp = new Date(unixtime * 1000);
    const shiftStartMoment = this.getTimeLineStartMoment();
    const shiftEndMoment = this.getTimeLineEndMoment();
    const shiftStartUnix = shiftStartMoment.unix();
    const shiftEndUnix = shiftEndMoment.unix();
    const shiftDurationSeconds = shiftEndUnix - shiftStartUnix;
    const barWidth = this.timeLineBar.nativeElement.getBoundingClientRect().width;
    if (shiftDurationSeconds > 0) {
      const percentage = Math.max(0, Math.min(100, ((unixtime - shiftStartUnix) / shiftDurationSeconds) * 100));
      this.markerPositionPx = barWidth * percentage / 100;
      this.markerFromEndPx = barWidth - this.markerPositionPx;
    } else {
      this.markerPositionPx = 0;
      this.markerFromEndPx = barWidth;
    }
    this.updateMarkers(barWidth, shiftStartMoment, shiftEndMoment);
  }

  onTimeLineClick(event: MouseEvent): void {
    // Get the bounding rectangle of the bar
    const rect = this.timeLineBar.nativeElement.getBoundingClientRect();

    // Calculate click position relative to the bar
    this.markerPositionPx = event.clientX - rect.left;
    this.barWidthPx = rect.width;
    this.markerFromEndPx = this.barWidthPx - this.markerPositionPx;

    // Calculate percentage (ensure it's between 0 and 100)
    const current = this.getCurrentMomentBasedOnMarkerPosition();
    this.currentTimestamp = current.toDate();

    // Stop playback
    if (this.playbackStatus === PlaybackStatus.PLAYING) {
      this.actionTriggered(PlaybackAction.STOP);
    }
    this.timeChanged.emit(current.unix());
  }

  private getCurrentMomentBasedOnMarkerPosition(): moment.Moment {
    const percentage = Math.max(0, Math.min(100, (this.markerPositionPx / this.barWidthPx) * 100));

    const shiftStartMoment = this.getTimeLineStartMoment();
    const numberOfSecondsSinceShiftStart = this.getTimeLineEndMoment().diff(shiftStartMoment, 'second') * percentage / 100;
    return shiftStartMoment.clone().add(numberOfSecondsSinceShiftStart, 'seconds');
  }

  getTimeLineMarkerHeight(): number {
    return (
      1 +
      (this.showPlowBar() ? 1 : 0) +
      (this.showSpreaderBar() ? 1 : 0) +
      (this.showSweepingBar() ? 1 : 0) +
      (this.showMowingBar() ? 1 : 0) +
      (this.showAssignmentsBar() ? 1 : 0)
    ) * 18;
  }

  showAssignmentsBar(): boolean {
    return !this.assignment && this.shiftAssignments && this.shiftAssignments.length > 0;
  }

  showPlowBar(): boolean {
    return this.shift.vehicleHardwareConfiguration?.sensorInputs
      ?.filter(i => i.type === SensorType.PLOW || i.type === SensorType.PLOW2W)
      ?.length > 0;
  }

  showSpreaderBar(): boolean {
    return this.shift.vehicleHardwareConfiguration?.sensorInputs
      ?.filter(i => i.type === SensorType.LIQUID_SPREADER || i.type === SensorType.GRANULAR_SPREADER
        || i.type === SensorType.DIGITAL_LIQUID_SPREADER || i.type === SensorType.DIGITAL_GRANULAR_SPREADER
      )
      ?.length > 0;
  }

  showSweepingBar(): boolean {
    return this.shift.vehicleHardwareConfiguration?.sensorInputs
      ?.filter(i => i.type === SensorType.SWEEPER)
      ?.length > 0;
  }

  showMowingBar(): boolean {
    return this.shift.vehicleHardwareConfiguration?.sensorInputs
      ?.filter(i => i.type === SensorType.MOWER)
      ?.length > 0;
  }

  onSpeedChanged(speed: number) {
    this.speed = speed;
    this.speedChanged.emit(speed);
  }

  actionTriggered(action: PlaybackAction) {
    switch (action) {
      case PlaybackAction.STOP:
        this.playbackStatus = PlaybackStatus.STOPPED;
        this.shiftMapContent.stopAnimation();
        break;
      case PlaybackAction.PLAY:
        this.playbackStatus = PlaybackStatus.LOADING;
        this.shiftMapContent.loadDataIfNeededAndPlay().then(response => {
          if (response) {
            this.playbackStatus = PlaybackStatus.PLAYING;
            this.shiftMapContent.pauseAnimation();
            this.shiftMapContent.jumpToPosition((this.time - this.getTimeLineStartMoment().unix()) || 0);
            this.shiftMapContent.playAnimation();
          } else {
            this.playbackStatus = PlaybackStatus.STOPPED;
            console.error('Something wrong happened with data load.');
          }
        }).catch(() => {
          this.playbackStatus = PlaybackStatus.STOPPED;
          // this.toast.long('Data load failed.');
        });
        break;
      default:
        this.playbackStatus = PlaybackStatus.STOPPED;
        this.shiftMapContent.stopAnimation();
    }
  }

  // Parse time string to minutes since midnight
  timeToMinutes(time: Moment): number {
    const [hours, minutes] = [time.hours(), time.minutes()];
    return hours * 60 + minutes;
  }

  // Convert minutes since midnight to time string
  minutesToTime(minutes: number): string {
    const h = Math.floor(minutes / 60) % 24;
    const m = minutes % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  }

  // Calculate interval in minutes based on duration and width
  getOptimalInterval(durationMinutes: number, width: number): number {
    const minSpacing = 80; // minimum pixels between markers
    const maxMarkers = Math.floor(width / minSpacing);

    // Possible intervals in minutes
    const intervals = [5, 10, 15, 20, 30, 60, 120, 180, 360, 720, 1440];

    for (const interval of intervals) {
      const numMarkers = Math.ceil(durationMinutes / interval);
      if (numMarkers <= maxMarkers) {
        return interval;
      }
    }

    return intervals[intervals.length - 1];
  }

  updateMarkers(width: number, startTime: Moment, endTime: Moment): void {
    const start = this.timeToMinutes(startTime);
    const end = this.timeToMinutes(endTime);
    const duration = end > start ? end - start : (1440 - start) + end;

    const interval = this.getOptimalInterval(duration, width);
    const markers: TimeAxisMarker[] = [];

    // Add start marker
    markers.push({
      position: 0,
      label: startTime.format('HH:mma'),
      isStart: true,
    });

    // Round start to nearest interval for intermediate markers
    const firstMarker = Math.ceil(start / interval) * interval;

    // Add intermediate markers
    for (let time = firstMarker; time < start + duration; time += interval) {
      const position = ((time - start) / duration) * 100;
      if (position > 0 && position < 100) {
        markers.push({
          position,
          label: this.minutesToTime(time)
        });
      }
    }

    // Add end marker
    markers.push({
      position: 100,
      label: endTime.format('HH:mma'),
      isEnd: true,
    });

    this.timeAxisMarkers = markers;
  }

  onMarkerMouseDown(event: MouseEvent): void {
    event.preventDefault();
    this.isDragging = true;

    // add global mouse event listeners
    document.addEventListener('mousemove', this.onMouseMove);
    document.addEventListener('mouseup', this.onMouseUp);
  }

  private onMouseMove = (event: MouseEvent): void => {
    if (!this.isDragging) {return;}

    const rect = this.timeLineBar.nativeElement.getBoundingClientRect();
    this.barWidthPx = rect.width;
    let newPosition = event.clientX - rect.left;
    newPosition = Math.max(0, Math.min(newPosition, this.barWidthPx));
    this.markerPositionPx = newPosition;
    this.markerFromEndPx = this.barWidthPx - newPosition;

    // calculate timestamp from percentage
    this.currentTimestamp = this.getCurrentMomentBasedOnMarkerPosition().toDate();
  };

  private onMouseUp = (): void => {
    if (!this.isDragging) {
      return;
    }

    const wasDragging = this.isDragging;
    this.isDragging = false;

    // remove global listeners
    document.removeEventListener('mousemove', this.onMouseMove);
    document.removeEventListener('mouseup', this.onMouseUp);

    // emit the final timestamp
    if (wasDragging) {
      this.timeChanged.emit(
        this.getCurrentMomentBasedOnMarkerPosition().unix()
      );
    }
  };
}
